import { useLayoutEffect, useMemo, useRef, Suspense, type ReactNode } from 'react';
import { createPortal, useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';

type CornerAlignment = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

interface ViewCubeHudProps {
    alignment: CornerAlignment;
    /** Distance from the canvas edges in pixels: [x, y]. */
    margin?: [number, number];
    /**
     * Render pass priority (same semantics as drei `Hud`):
     * - 1 (default): full takeover — renders the main scene first, then the
     *   cube layer. Use when no other priority-1 renderer exists.
     * - > 1: cube layer only, drawn on top of an earlier priority pass
     *   (e.g. an EffectComposer at priority 1).
     */
    renderPriority?: number;
    children: ReactNode;
}

/**
 * Scoped to the HUD portal: swaps the portal layer's default camera to the
 * orthographic HUD camera (the same thing drei's `OrthographicCamera
 * makeDefault` does, without the unused render-target overhead). This keeps
 * pointer events on the cube raycasting in pixel space with the HUD camera,
 * while the root store's camera stays the real viewport camera.
 */
function HudCamera({ camera }: { camera: THREE.OrthographicCamera }) {
    const set = useThree((state) => state.set);
    const get = useThree((state) => state.get);
    useLayoutEffect(() => {
        const previous = get().camera;
        set({ camera });
        return () => set({ camera: previous });
    }, [camera, set, get]);
    return null;
}

/**
 * Deterministic HUD layer for the ViewCube.
 *
 * Replaces drei's `GizmoHelper` + `Hud` combination. Their render takeover
 * (a priority-1 `useFrame` registered from inside a portal store) silently
 * stops drawing the cube when subscriber ordering or portal store behavior
 * shifts — drei's docs explicitly warn: "use renderPriority to prevent the
 * helper from disappearing if there is another useFrame(..., 1)".
 *
 * This component keeps the proven parts of that pattern (a portal-scoped
 * orthographic camera so pointer events raycast the cube layer correctly)
 * but registers ONE priority-1 frame callback at the root level that renders
 * the main scene and the cube layer back-to-back, so the cube can never be
 * wiped by another subscriber or lost to subscriber registration subtleties.
 *
 * Compatible with `frameloop="demand"`: the callback runs on every
 * invalidated frame, keeping the cube in sync with the main camera.
 */
export function ViewCubeHud({
    alignment,
    margin = [80, 80],
    renderPriority = 1,
    children,
}: ViewCubeHudProps) {
    const gl = useThree((state) => state.gl);
    const size = useThree((state) => state.size);
    const invalidate = useThree((state) => state.invalidate);

    const [virtualScene] = useMemo(() => [new THREE.Scene()], []);
    const virtualCam = useMemo(() => {
        const cam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 1000);
        cam.position.set(0, 0, 200);
        return cam;
    }, []);
    const gizmoRef = useRef<THREE.Group>(null);
    const inverseMatrix = useMemo(() => new THREE.Matrix4(), []);
    // Scratch quaternion reused every frame (was a fresh `new THREE.Quaternion()`
    // per invalidated frame — steady GC churn while the cube is visible).
    const scratchQuaternion = useMemo(() => new THREE.Quaternion(), []);

    // Keep the virtual camera's frustum in lock-step with the canvas size
    // (pixel-space: 1 world unit = 1 CSS pixel, so margins align with UI).
    // Layout effect + invalidate: the frustum must be correct before the very
    // first demand-mode frame, and a resize must schedule a new frame.
    useLayoutEffect(() => {
        virtualCam.left = -size.width / 2;
        virtualCam.right = size.width / 2;
        virtualCam.top = size.height / 2;
        virtualCam.bottom = -size.height / 2;
        virtualCam.updateProjectionMatrix();
        invalidate();
    }, [virtualCam, size, invalidate]);

    const [marginX, marginY] = margin;
    const x = alignment.endsWith('-left') ? -size.width / 2 + marginX : size.width / 2 - marginX;
    const y = alignment.startsWith('top-') ? size.height / 2 - marginY : -size.height / 2 + marginY;

    useFrame((state) => {
        // 1) Sync the cube's orientation with the main camera so it always
        //    faces the viewer and mirrors the current orbit orientation.
        if (gizmoRef.current) {
            inverseMatrix.copy(state.camera.matrixWorld).invert();
            const q = scratchQuaternion.setFromRotationMatrix(inverseMatrix);
            // Guard against degenerate camera matrices (e.g. plan view with up ∥ forward)
            // which produce NaN quaternions and hide the cube entirely.
            if (isFinite(q.x) && isFinite(q.y) && isFinite(q.z) && isFinite(q.w)) {
                gizmoRef.current.quaternion.copy(q);
            }
        }

        const previousAutoClear = gl.autoClear;

        // 2) Full takeover (priority 1): paint the main scene first, exactly
        //    like drei Hud's RenderHud does. Higher priorities assume an
        //    earlier pass already painted the scene.
        if (renderPriority === 1) {
            gl.autoClear = true;
            gl.render(state.scene, state.camera);
        }

        // 3) Draw the cube layer on top: clear depth only, so the cube
        //    overlays the scene without wiping it.
        //    The main scene's last-drawn transparent material (depthWrite:
        //    false) can leave the GL depth mask off, which silently turns
        //    clearDepth() into a no-op and hides the cube behind the scene's
        //    depth buffer. Force the mask back on through three's state API
        //    (keeps its internal cache consistent) before clearing.
        //    The renderer's GLOBAL clipping planes (plan view range, crop
        //    region) must NOT apply to the HUD: the cube lives in pixel-space
        //    coordinates of its own virtual scene and would otherwise be
        //    sliced away by planes meant for the model.
        gl.autoClear = false;
        gl.state.buffers.depth.setMask(true);
        gl.clearDepth();

        const previousClipPlanes = gl.clippingPlanes;
        gl.clippingPlanes = [];
        gl.render(virtualScene, virtualCam);
        gl.clippingPlanes = previousClipPlanes;
        gl.autoClear = previousAutoClear;
    }, renderPriority);

    return createPortal(
        <>
            {/* Portal-scoped default camera (see HudCamera) — pointer events
                on the cube raycast with this orthographic camera. */}
            <HudCamera camera={virtualCam} />
            {/* Event anchor required so the portal layer's event handler
                engages (same trick drei Hud uses). */}
            <group onPointerOver={() => null} />
            <group ref={gizmoRef} position={[x, y, 0]}>
                {/* Cube content may contain async troika Text; suspend locally
                    so a pending font load never blanks the whole canvas. */}
                <Suspense fallback={null}>{children}</Suspense>
            </group>
        </>,
        virtualScene,
        { events: { priority: renderPriority + 1 } }
    );
}
